<script setup lang="ts">
import UIBody from '@/components/ui/UIBody.vue'
import UIToolTip from '@/components/ui/UIToolTip.vue'
import useFullRemedies from '@/composables/data/useFullRemedies'
import { useAnalytics } from '@/composables/useAnalytics'
import { useInlineLanguage } from '@/composables/useLanguage'
import { useGetDataLayers } from '@/services/adapters/useGetDataLayers'
import { useGetEffects } from '@/services/adapters/useGetEffects'
import { useReferencesStore } from '@/store/references'
import {
  IPillLinkItem,
  TTooltipAlignment,
  TTooltipDirection
} from '@lifk/types'
import { computed } from 'vue'

const props = defineProps<{
  item: IPillLinkItem
  tooltipDirection: TTooltipDirection
  tooltipAlignment: TTooltipAlignment
  /** Should the tooltip be disabled (primarily for link items that appear within a tooltip already)? */
  tooltipDisabled?: boolean
}>()

const { t } = useInlineLanguage()
const referencesStore = useReferencesStore()
const { data: effects } = useGetEffects()
const { fullRemedies: remedies } = useFullRemedies(false) // filter out deprecated remedies
const { data: dataLayers } = useGetDataLayers()
const { trackButtonClick } = useAnalytics()

const clicksEnabled = computed(() => {
  switch (props.item.linkType) {
    case 'remedy':
      return !!remedies.value?.find(({ key }) => key === props.item.key)
    case 'effect':
      return !!effects.value?.find(({ key }) => key === props.item.key)
    case 'dataLayer':
      return !!dataLayers.value?.find(({ key }) => key === props.item.key)
    default:
      return false
  }
})

const referencesLinkClicked = () => {
  if (clicksEnabled.value) {
    referencesStore.openReference(
      props.item.linkType,
      props.item.key,
      'references'
    )
    trackButtonClick({
      action: 'viewed_guidebook',
      location: 'ui_pill_link_item',
      value: {
        key: props.item.key,
        name: props.item.name['en-us']
      }
    })
  }
}
</script>

<template>
  <div class="w-fit">
    <UIToolTip
      :direction="tooltipDirection"
      :alignment="tooltipAlignment"
      variant="light"
      :disabled="tooltipDisabled"
      :reference="item"
      class="group rounded"
    >
      <UIBody
        weight="medium"
        :style="[
          { '--underline-color': item.underlineColor },
          { '--pill-color': item.pillColor },
          { '--text-color': item.textColor }
        ]"
        class="underline-pill w-fit relative z-2 cursor-pointer text-left whitespace-nowrap"
        tabindex="0"
        role="link"
        @click.stop.prevent="referencesLinkClicked"
      >
        {{ t(item.name) }}
      </UIBody>
    </UIToolTip>
  </div>
</template>

<style scoped>
.underline-pill {
  @apply after:absolute after:content-[''] after:z-[-1]
    after:bg-[color:var(--underline-color)] after:rounded-full
    after:bottom-0 after:left-[-3px] after:h-0.5 after:w-[calc(100%+6px)] mx-[3px] mb-0.5;
  @apply after:hover:transition-colors after:hover:transition-[height];
  @apply hover:text-[color:var(--text-color)] hover:after:bg-[color:var(--pill-color)]
    hover:after:bottom-[-2px] hover:after:h-[calc(100%+4px)]
    hover:after:rounded-xs;
}
</style>
